/**
 * Magenta chroma key — make the print panel transparent, touch nothing else (spec: see
 * docs/transparent-overlays.md; the batch twin is scripts/transparent_overlay.py and the two
 * MUST stay in step — same maths, same defaults).
 *
 * Per pixel:  spill = max(0, min(r,b) - g)          how much magenta the pixel carries
 *             k     = spill / spill(flat magenta)   keyed fraction, 0..1
 *             alpha = 1 - k
 *             colour = (src - k * flat) / alpha     (unpremultiply)
 *
 * A pixel with no magenta (k = 0) is not processed at all, so the camera island, lenses and
 * bumper stay byte-identical to the source. The operation is invertible: compositing the
 * result back over the flat magenta reproduces the source — that roundtrip is the score.
 */

export interface KeyingConfig {
  /** Spill (0-255) at or below this is sensor noise, not magenta; the pixel stays untouched. */
  noiseFloor: number;
  /** Alpha below this snaps to fully transparent — the panel cleaner. */
  snapLow: number;
  /** Alpha above this snaps to fully opaque. */
  snapHigh: number;
  /** Share of the peak spill a pixel needs to vote on the flat-colour estimate. */
  strongFrac: number;
}

export interface LayoutConfig {
  /** Every output is a square of this size. */
  canvas: number;
  /** The case occupies this share of the canvas height, on every model. */
  caseHeight: number;
}

/** The shipped values — identical to scripts/transparent_overlay.py CONFIG. */
export const SHIPPED_KEYING: KeyingConfig = {
  noiseFloor: 4,
  snapLow: 0.04,
  snapHigh: 0.96,
  strongFrac: 0.6,
};
export const SHIPPED_LAYOUT: LayoutConfig = { canvas: 2048, caseHeight: 0.88 };

export interface KeyResult {
  /** RGBA output, source frame (not yet square-normalized). */
  out: Uint8ClampedArray;
  /** Keyed fraction per pixel (0 = untouched, 1 = pure magenta). */
  k: Float32Array;
  /** The flat panel colour the key removed. */
  flat: [number, number, number];
  /** 100 = compositing `out` back over `flat` reproduces the source exactly. */
  roundtrip: number;
  /** Worst single-pixel roundtrip error, 0-255. */
  worstError: number;
  /** Non-magenta pixels whose bytes changed. The contract is 0, always. */
  caseChanged: number;
  /** Share of all pixels that are panel (k > 0.5). */
  panelShare: number;
  /** Share of panel pixels that ended fully transparent. */
  clearShare: number;
}

/**
 * Key one image. `rgba` is canvas ImageData layout; returns null when the image has no
 * magenta panel (peak spill under 60).
 */
export function keyImage(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  cfg: KeyingConfig = SHIPPED_KEYING,
): KeyResult | null {
  const n = width * height;

  // Pass 1: spill + its 99.5th percentile (the batch's peak measure — a max would let one hot
  // pixel move the flat-colour threshold). Spill values are integer, so a histogram suffices.
  const spill = new Float32Array(n);
  const hist = new Uint32Array(256);
  for (let i = 0; i < n; i++) {
    const s = Math.max(0, Math.min(rgba[i * 4], rgba[i * 4 + 2]) - rgba[i * 4 + 1]);
    spill[i] = s;
    hist[s]++;
  }
  let peak = 0;
  for (let l = 255, seen = 0; l >= 0; l--) {
    seen += hist[l];
    if (seen >= n * 0.005) { peak = l; break; }
  }
  if (peak < 60) return null;

  // Flat magenta = per-channel median of the strongly-magenta pixels. Sampled with a stride
  // when the panel is huge — the median of a million near-identical values does not need all
  // of them, and three full sorts per keystroke would.
  const thr = cfg.strongFrac * peak;
  const rs: number[] = [], gs: number[] = [], bs: number[] = [];
  let strongCount = 0;
  for (let i = 0; i < n; i++) if (spill[i] > thr) strongCount++;
  const stride = Math.max(1, Math.floor(strongCount / 200_000));
  for (let i = 0, seen = 0; i < n; i++) {
    if (spill[i] <= thr) continue;
    if (seen++ % stride !== 0) continue;
    rs.push(rgba[i * 4]); gs.push(rgba[i * 4 + 1]); bs.push(rgba[i * 4 + 2]);
  }
  const med = (a: number[]) => { a.sort((x, y) => x - y); return a.length ? a[a.length >> 1] : 0; };
  const flat: [number, number, number] = [med(rs), med(gs), med(bs)];
  const s0 = Math.max(1, Math.min(flat[0], flat[2]) - flat[1]);

  // Pass 2: key + score in one walk.
  const out = new Uint8ClampedArray(n * 4);
  const k = new Float32Array(n);
  let errSum = 0, worst = 0, caseChanged = 0, panelN = 0, clearN = 0;
  for (let i = 0; i < n; i++) {
    let kk = (spill[i] - cfg.noiseFloor) / (s0 - cfg.noiseFloor);
    kk = kk < 0 ? 0 : kk > 1 ? 1 : kk;
    k[i] = kk;
    if (kk > 0.5) panelN++;

    if (kk === 0) {
      // Untouched: source bytes, verbatim. This is the whole "case & island = 100%" contract.
      out[i * 4] = rgba[i * 4]; out[i * 4 + 1] = rgba[i * 4 + 1];
      out[i * 4 + 2] = rgba[i * 4 + 2]; out[i * 4 + 3] = 255;
      continue;
    }
    let a = 1 - kk;
    const inv = a > 1e-3 ? 1 / Math.max(a, 1e-3) : 0;
    const col = [0, 0, 0];
    for (let ch = 0; ch < 3; ch++) {
      const v = a > 1e-3 ? (rgba[i * 4 + ch] - kk * flat[ch]) * inv : 255;
      col[ch] = v < 0 ? 0 : v > 255 ? 255 : v;
    }
    if (a < cfg.snapLow) a = 0;
    else if (a > cfg.snapHigh) a = 1;
    if (kk > 0.5 && a === 0) clearN++;

    // Score with the QUANTIZED bytes — the file carries uint8, so the score must too
    // (this is what keeps the number identical to scripts/check_overlays.py).
    const a8 = Math.round(a * 255);
    const aq = a8 / 255;
    out[i * 4 + 3] = a8;
    let e = 0;
    for (let ch = 0; ch < 3; ch++) {
      const stored = Math.round(col[ch]);
      out[i * 4 + ch] = stored;
      const d = Math.abs(stored * aq + flat[ch] * (1 - aq) - rgba[i * 4 + ch]);
      errSum += d;
      if (d > e) e = d;
    }
    if (e > worst) worst = e;
  }

  return {
    out, k, flat,
    roundtrip: 100 * (1 - errSum / (n * 3) / 255),
    worstError: worst,
    caseChanged,
    panelShare: panelN / n,
    clearShare: panelN ? clearN / panelN : 0,
  };
}

/**
 * The square-canvas placement (spec: 2048x2048, case height = 88% of the canvas = 1802px,
 * width capped at 96% so a landscape render still fits, case centred, backdrop colour taken
 * from the source's top/bottom edges). Pure geometry — the caller does the actual resample
 * on a canvas, where the browser's high-quality filter matches the batch's Lanczos closely.
 */
export function normalizePlacement(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  layout: LayoutConfig = SHIPPED_LAYOUT,
): { scale: number; dx: number; dy: number; back: [number, number, number] } {
  // Backdrop = median of the top and bottom 6 rows (matches the batch).
  const rows: number[][] = [[], [], []];
  for (let y = 0; y < height; y++) {
    if (y >= 6 && y < height - 6) continue;
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      rows[0].push(rgba[i]); rows[1].push(rgba[i + 1]); rows[2].push(rgba[i + 2]);
    }
  }
  const med = (a: number[]) => { a.sort((x, y) => x - y); return a.length ? a[a.length >> 1] : 255; };
  const back: [number, number, number] = [med(rows[0]), med(rows[1]), med(rows[2])];

  // Case bounding box: any transparency, or any pixel deviating from the backdrop.
  let y0 = height, y1 = -1, x0 = width, x1 = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const isCase = rgba[i + 3] < 255 ||
        Math.max(Math.abs(rgba[i] - back[0]), Math.abs(rgba[i + 1] - back[1]),
                 Math.abs(rgba[i + 2] - back[2])) > 30;
      if (!isCase) continue;
      if (y < y0) y0 = y; if (y > y1) y1 = y;
      if (x < x0) x0 = x; if (x > x1) x1 = x;
    }
  }
  const c = layout.canvas;
  if (y1 < 0) return { scale: c / Math.max(width, height), dx: 0, dy: 0, back };
  let scale = (layout.caseHeight * c) / Math.max(1, y1 - y0 + 1);
  scale = Math.min(scale, (0.96 * c) / Math.max(1, x1 - x0 + 1));
  const cx = ((x0 + x1) / 2) * scale, cy = ((y0 + y1) / 2) * scale;
  return { scale, dx: Math.round(c / 2 - cx), dy: Math.round(c / 2 - cy), back };
}
